import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import { useAlert } from '../../context/AlertContext';
import { useUser } from '../../context/UserContext';
import { ACCESS_TOKEN_KEY } from '../../utils/auth';
import Logo from '../Global/Logo';

export default function AuthForm({ login = true }) {
	const { setIsLoggedIn, isLoggedIn, setUser, setLoginPending } = useUser();
	const { setAlert } = useAlert();
	const navigate = useNavigate();

	const validate = (values) => {
		const errors = {};
		if (!values.email) errors.email = 'Email is missing.';
		else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email))
			errors.email = 'Invalid email address.';
		if (!values.password) errors.password = 'Password is missing.';
		else if (values.password.length < 8)
			errors.password = 'Password must be at least 8 characters.';
		if (!login && !values.name) errors.name = 'Name is missing.';
		return errors;
	};

	const handleAuth = async (e) => {
		try {
			e.preventDefault();
			const values = {
				email: e.target.email.value,
				password: e.target.password.value,
				...(login ? {} : { name: e.target.name.value }),
			};

			const errors = validate(values);
			if (Object.keys(errors).length > 0) {
				setAlert('error', Object.values(errors).join(', '));
				return;
			}

			setLoginPending(true);
			const endpoint = login ? '/user/login' : '/user/signup';
			const { data } = await client.post(endpoint, { ...values });

			if (!data.success) {
				setAlert('error', data.error);
				return console.error(data.error);
			}

			setLoginPending(false);
			setUser(data.user);
			if (data.token) sessionStorage.setItem(ACCESS_TOKEN_KEY, data.token);
			setIsLoggedIn(true);

			navigate('/admin');
		} catch (error) {
			setLoginPending(false);
			setAlert('error', error?.response?.data?.error || 'An error occurred');
			console.error(error?.response?.data?.error);
		}
	};

	useEffect(() => {
		if (window.tm)
			window.tm.event('pageView', 'AuthForm', {
				page: {
					pageInfo: {
						pageName: `AuthForm: ${login ? 'Login' : 'Signup'}`,
						sysEnv: window.navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop',
						window: {
							width: window.innerWidth,
							height: window.innerHeight,
						},
						variant: 'A',
						version: '1.0',
					},
				},
			});
	}, []);
	// if (isLoggedIn) {
	// 	navigate('/dashboard');
	// 	return null;
	// }

	return (
		<div className='bg-gray-50 h-screen'>
			<div className='flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8'>
				<div className='sm:mx-auto sm:w-full sm:max-w-md'>
					<Logo className='mx-auto h-24 w-auto' />
					<h2 className='mt-6 text-center text-2xl font-bold text-gray-900'>
						{login ? 'Sign in to your account' : 'Create an account'}
					</h2>
				</div>

				<div className='mt-10 sm:mx-auto sm:w-full sm:max-w-md'>
					<div className='bg-white py-8 px-6 shadow sm:rounded-lg sm:px-10'>
						<form
							onSubmit={handleAuth}
							className='space-y-6'
						>
							{!login && (
								<div>
									<label
										htmlFor='name'
										className='block text-sm font-medium text-gray-700'
									>
										Name
									</label>
									<input
										id='name'
										name='name'
										type='text'
										required={!login}
										className='mt-1 py-3 px-3 block w-full rounded-md border-gray-3 px-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm'
									/>
								</div>
							)}
							<div>
								<label
									htmlFor='email'
									className='block text-sm font-medium text-gray-700'
								>
									Email address
								</label>
								<input
									id='email'
									name='email'
									type='email'
									autoComplete='email'
									required
									className='mt-1 block py-3 px-3  w-full rounded-md border-gray-3 px-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm'
								/>
							</div>

							<div>
								<label
									htmlFor='password'
									className='block text-sm font-medium text-gray-700'
								>
									Password
								</label>
								<input
									id='password'
									name='password'
									type='password'
									autoComplete='current-password'
									required
									className='mt-1 block w-full py-3 px-3 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm'
								/>
							</div>

							<div className='flex items-center justify-between'>
								<div className='flex items-center'>
									<input
										id='remember-me'
										name='remember-me'
										type='checkbox'
										className='h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded'
									/>
									<label
										htmlFor='remember-me'
										className='ml-2 block text-sm text-gray-900'
									>
										Remember me
									</label>
								</div>

								<div className='text-sm'>
									<a
										href='#'
										className='font-medium text-primary-600 hover:text-primary-500'
									>
										Forgot your password?
									</a>
								</div>
							</div>

							<div>
								<button
									type='submit'
									className='w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
								>
									{login ? 'Sign in' : 'Sign up'}
								</button>
							</div>
						</form>
					</div>
					<p className='mt-2 text-center text-sm text-gray-600'>
						{login ? 'Not a member? ' : 'Already have an account? '}
						<a
							href='#'
							onClick={() => navigate(login ? '/signup' : '/login')}
							className='font-medium text-primary-600 hover:text-primary-500'
						>
							{login ? 'Sign up' : 'Sign in'}
						</a>
					</p>
				</div>
			</div>
		</div>
	);
}
